<template>
    <div class="w-full h-full flex flex-col p-3 pt-4 space-y-2">
        <date-picker-v2 v-model="atDate" class="w-30"/>
        <div class="flex justify-center h-full">
            <ag-grid-vue
                class="w-full h-full max-w-screen-2xl"
                :pinned-top-row-data="pinnedTopRowData"
                :row-data="agGridRows"
                :column-defs="colDefs"
                :theme="agTheme"
                :grid-options="gridOptions"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, Ref, ref, shallowRef, watch} from 'vue'
import {useSubAccounts} from '@/subaccount/subAccount.service'
import {AgeDividedReportEntry, CUSTOMER, SubAccountRelationType} from '@/subaccount/types/SubAccount'
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {formatCurrency} from '@/services/formatter.service'
import SubAccountNameCell from '@/subaccount/components/SubAccountNameCell.vue'
import {DELETE_TRANSACTION, SACC_LINES_UPDATED, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import {GridApi, GridOptions, GridReadyEvent, RowClassParams} from 'ag-grid-community'
import {get} from '@/services/infrastructure/api.service'
import {toIsoDate} from '@/dateUtils/dateUtils'
import DatePickerV2 from '@/dateUtils/DatePickerV2.vue'

const atDate = ref<Date>(new Date())
const report = ref<AgeDividedReportEntry[]>()
const gridApi = shallowRef<GridApi>()

const agGridRows = computed(() => {
    if (!report.value) return []
    return report.value
        .filter(entry => !!entry.subAccountUuid) // Exclude sum row from regular rows
        .map((entry: AgeDividedReportEntry) => {
            const sa = subAccounts.value.find(s => s.uuid === entry.subAccountUuid)
            return {
                ...sa,
                ...entry.sums
            }
        })
})

const pinnedTopRowData = computed(() => {
    if (!report.value) return []
    const sumEntry = report.value.find(entry => !entry.subAccountUuid)
    return sumEntry ? [{
        name: 'Sum',
        type: 'summary',
        ...sumEntry.sums
    }] : []
})

const {onEvents, cleanUp} = useEmitter()

const props = defineProps<{
    relationType: SubAccountRelationType
}>()

const panelId: Ref<number> | undefined = inject('panelId')

const subAccounts = props.relationType === CUSTOMER ? useSubAccounts().customers : useSubAccounts().suppliers

const gridOptions: GridOptions = {
    context: {
        panelId
    },
    animateRows: true,
    getRowId: (params: any) => params.data.name,
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
        window.gridApi = gridApi.value
    },
    getRowClass: (params: RowClassParams) => {
        if (['summary'].includes(params.data?.type)) {
            return '!bg-base-100'
        }
        if (['percentage'].includes(params.data?.type)) {
            return '!bg-base-100 !border-base-300'
        }
    }
}

const valueFormatter = (params: any) => params.value ? formatCurrency(params.value, 0) : ''
const defaultColDef = {
    flex: 1,
    minWidth: 100,
    maxWidth: 260,
    headerClass: 'header-right',
    cellStyle: {'text-align': 'right'},
    valueFormatter
}
const colDefs = ([
    {
        headerName: props.relationType === CUSTOMER ? 'Kunde' : 'Leverandør',
        field: 'name',
        cellRenderer: SubAccountNameCell,
        contextMenuItems: [],
        flex: 1,
        minWidth: 140,
        maxWidth: undefined
    },
    {
        field: 'Not due',
        headerName: 'Ikke forfalt',
        ...defaultColDef
    },
    {
        field: '< 15',
        headerName: '< 15',
        ...defaultColDef
    },
    {
        field: '15-30',
        headerName: '15-30',
        ...defaultColDef
    },
    {
        field: '30-60',
        headerName: '30-60',
        ...defaultColDef
    },
    {
        field: '60-90',
        headerName: '60-90',
        ...defaultColDef
    },
    {
        field: '90-180',
        headerName: '90-180',
        ...defaultColDef
    }, {
        field: '> 180',
        headerName: '> 180',
        ...defaultColDef
    },
    {
        field: 'total',
        headerName: 'Total',
        ...defaultColDef
    }
])

const fetchData = async () => {
    report.value = await get<AgeDividedReportEntry[]>(`/sub-accounts/${props.relationType}/age-divided-entries?date=${toIsoDate(atDate.value)}`)
}

const clearData = () => report.value = []

watch(atDate, fetchData)

onMounted(async () => {
    await fetchData()
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION, ORG_SELECTED, SACC_LINES_UPDATED], async () => {
        clearData()
        await fetchData()
        gridApi.value?.redrawRows()
    })
})

onUnmounted(cleanUp)

</script>
